import { Capacitor, registerPlugin } from '@capacitor/core';

const STORAGE_KEY = 'mt_driver_play_review_v1';
const MAX_AUTOMATIC_ATTEMPTS = 2;
const MIN_ATTEMPT_GAP_MS = 60 * 24 * 60 * 60 * 1000;
// A queued trip is only worth acting on close to when it finished. Past this the
// "you just hit a milestone" moment is gone — drop it so a permanently-stuck
// backend count can never keep a pending record (or its checks) alive forever.
const MAX_PENDING_AGE_MS = 3 * 24 * 60 * 60 * 1000;
const MILESTONES = new Set([10]);
const PlayReview = registerPlugin('PlayReview');

const isAndroidNative = () => {
  try { return Capacitor.getPlatform?.() === 'android' && Capacitor.isNativePlatform?.(); } catch { return false; }
};

const readState = (uid) => {
  try {
    const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    return value?.uid === uid
      ? { uid, attempts: Array.isArray(value.attempts) ? value.attempts : [], pending: value.pending || null }
      : { uid, attempts: [], pending: null };
  } catch {
    return { uid, attempts: [], pending: null };
  }
};

const writeState = (state) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* storage unavailable */ }
};

/** Queue a just-completed trip; Dashboard decides whether it is safe to ask. */
export const queuePlayReviewForCompletedTrip = ({ uid, rideId, targetCompletedRides }) => {
  if (!uid || !rideId || !Number.isFinite(Number(targetCompletedRides))) return;
  const state = readState(uid);
  state.pending = {
    rideId: String(rideId),
    queuedAt: Date.now(),
    targetCompletedRides: Math.floor(Number(targetCompletedRides)),
  };
  writeState(state);
};

/**
 * Runs only on the idle, visible Dashboard after DriverContext receives the
 * backend's completion aggregate. It never runs during a trip or booking.
 */
export const requestQueuedPlayReview = async ({ uid, completedRides }) => {
  if (!uid) return { state: 'none' };
  const state = readState(uid);
  const pending = state.pending;
  if (!pending) return { state: 'none' };

  const queuedAt = Number(pending.queuedAt);
  if (!Number.isFinite(queuedAt) || Date.now() - queuedAt > MAX_PENDING_AGE_MS) {
    state.pending = null;
    writeState(state);
    return { state: 'stale' };
  }

  const target = Number(pending.targetCompletedRides);
  const count = Math.max(0, Math.floor(Number(completedRides) || 0));
  if (!MILESTONES.has(target)) {
    state.pending = null;
    writeState(state);
    return { state: 'not_milestone' };
  }
  if (count < target) return { state: 'waiting' };

  state.pending = null;
  if (!isAndroidNative()) {
    writeState(state);
    return { state: 'not_native' };
  }

  const now = Date.now();
  const lastAttemptAt = Math.max(0, ...state.attempts.map((attempt) => Number(attempt?.at) || 0));
  if (state.attempts.length >= MAX_AUTOMATIC_ATTEMPTS || (lastAttemptAt && now - lastAttemptAt < MIN_ATTEMPT_GAP_MS)) {
    writeState(state);
    return { state: 'throttled' };
  }

  state.attempts.push({ at: now, milestone: target, rideId: pending.rideId });
  writeState(state);
  try {
    await PlayReview.requestReview();
  } catch (err) {
    console.warn('Play review request unavailable:', err?.message || err);
  }
  return { state: 'attempted' };
};

/** Explicit Profile action only. */
export const openPlayStoreListing = async () => {
  if (isAndroidNative()) {
    try {
      await PlayReview.openStoreListing();
      return true;
    } catch (err) {
      console.warn('Play Store link unavailable:', err?.message || err);
    }
  }
  if (typeof window !== 'undefined') {
    window.open('https://play.google.com/store/apps/details?id=in.mytownly.rides.driver', '_blank', 'noopener,noreferrer');
  }
  return false;
};
